import dayjs from 'dayjs';

// material-ui
import { Box, Typography } from '@mui/material';

import TotalGrowthBarChart from './TotalGrowthBarChart';
import TotalGrowthAreaChart from './TotalGrowthAreaChart';
import TotalGrowthPieChart from './TotalGrowthPieChart';
import TotalItemCard from './TotalItemCard';
import { Groups2Outlined, HailOutlined, SupportAgentOutlined } from '@mui/icons-material';

import TotalDonutChart from './TotalDonutChart';

interface SystemTabProps {
  isLoading: boolean;
  totalCustomersData: Array<number>;
  totalDriverCount: Array<number>;
  totalVehiclesCount: Array<number>;
  totalTicketsResolved: number;
  totalTicketsUnresolved: number;
  totalDriversPerCnh: Array<{}>;
  totalVehiclesPerBrand: Array<{}>;
  totalTicketsByGravity: Array<{}>;
  totalVehiclesByCustomer: Array<{}>;
  totalDevicesByManufacturer: Array<{}>;
  totalVehiclesByStatus: Array<{}>;
}

const SystemTab = ({
  isLoading,
  totalCustomersData,
  totalDriverCount,
  totalVehiclesCount,
  totalTicketsResolved,
  totalTicketsUnresolved,
  totalDriversPerCnh,
  totalVehiclesPerBrand,
  totalTicketsByGravity,
  totalVehiclesByCustomer,
  totalDevicesByManufacturer,
  totalVehiclesByStatus
}: SystemTabProps) => {
  const chartGridSx = {
    display: 'grid',
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
    gap: 1.5,
    width: '100%',
  };

  const monthlyGridSx = {
    ...chartGridSx,
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' },
  };

  return (
    <Box className="tw-flex tw-w-full tw-min-w-0 tw-flex-col tw-gap-3 tw-px-2 tw-pb-4 sm:tw-px-3">

      {/* ROW 1: CARDS NUMÉRICOS NO TOPO */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5, width: '100%' }}>
          <Box className="tw-min-w-0 tw-text-center">
            <TotalItemCard withAdornment={true} isLoading={isLoading} title={"de Clientes"}>
              <Box className="tw-relative tw-mx-auto tw-flex tw-w-fit tw-justify-center">
                <Box className="tw-flex tw-h-32 tw-w-32 tw-items-center tw-justify-center tw-rounded-full tw-bg-white/10 tw-ring-1 tw-ring-white/20">
                  <Typography sx={{ fontSize: '42px', fontWeight: 600 }}>{`${totalCustomersData[dayjs().month()]}`}</Typography>
                </Box>
                <Groups2Outlined className="tw-absolute -tw-right-3 tw-top-1" sx={{ height: 36, width: 36 }} />
              </Box>
            </TotalItemCard>
          </Box>

          <Box className="tw-min-w-0 tw-text-center">
            <TotalItemCard withAdornment={true} isLoading={isLoading} title={"de Chamados Resolvidos"}>
              <Box className="tw-relative tw-mx-auto tw-flex tw-w-fit tw-justify-center">
                <Box className="tw-flex tw-h-32 tw-w-32 tw-items-center tw-justify-center tw-rounded-full tw-bg-white/10 tw-ring-1 tw-ring-white/20">
                  <Typography sx={{ fontSize: '42px', fontWeight: 600 }}>{`${totalTicketsResolved}`}</Typography>
                </Box>
                <SupportAgentOutlined className="tw-absolute -tw-right-3 tw-top-1" sx={{ height: 36, width: 36 }} />
              </Box>
            </TotalItemCard>
          </Box>

          <Box className="tw-min-w-0 tw-text-center">
            <TotalItemCard withAdornment={true} isLoading={isLoading} title={"de Chamados Pendentes"}>
              <Box className="tw-relative tw-mx-auto tw-flex tw-w-fit tw-justify-center">
                <Box className="tw-flex tw-h-32 tw-w-32 tw-items-center tw-justify-center tw-rounded-full tw-bg-white/10 tw-ring-1 tw-ring-white/20">
                  <Typography sx={{ fontSize: '42px', fontWeight: 600 }}>{`${totalTicketsUnresolved}`}</Typography>
                </Box>
                <HailOutlined className="tw-absolute -tw-right-3 tw-top-1" sx={{ height: 36, width: 36 }} />
              </Box>
            </TotalItemCard>
          </Box>
      </Box>

      {/* ROW 2: OS 3 GRÁFICOS SOLICITADOS */}
      <Box sx={monthlyGridSx}>
          <Box className="tw-min-w-0">
            <TotalGrowthPieChart
              isLoading={isLoading}
              dataChartCustomersList={totalCustomersData}
              dataCharDriverList={totalDriverCount}
              dataChartVehiclesList={totalVehiclesCount}
            />
          </Box>
          <Box className="tw-min-w-0">
            <TotalGrowthAreaChart
              isLoading={isLoading}
              dataChartCustomersList={totalCustomersData}
              dataCharDriverList={totalDriverCount}
              dataChartVehiclesList={totalVehiclesCount}
            />
          </Box>
          <Box className="tw-min-w-0">
            <TotalGrowthBarChart
              isLoading={isLoading}
              dataChartCustomersList={totalCustomersData}
              dataCharDriverList={totalDriverCount}
              dataChartVehiclesList={totalVehiclesCount}
            />
          </Box>
      </Box>
      {/*-----------------------*/}

      <Box sx={chartGridSx}>
          <Box className="tw-min-w-0 tw-text-center">
            <TotalItemCard withAdornment={false} isLoading={isLoading} title={"de Veículos por Marca"}>
              <Box className="tw-flex tw-w-full tw-min-w-0 tw-justify-center">
                <TotalDonutChart data={totalVehiclesPerBrand.map((it: any) => it.vehicleCount)} labels={totalVehiclesPerBrand.map((it: any) => it.brandName)} type="pie" isLoading={isLoading} />
              </Box>
            </TotalItemCard>
          </Box>

          <Box className="tw-min-w-0 tw-text-center">
            <TotalItemCard withAdornment={false} isLoading={isLoading} title={"de Motoristas por Categoria"}>
              <Box className="tw-flex tw-w-full tw-min-w-0 tw-justify-center">
                <TotalDonutChart data={totalDriversPerCnh.map((it: any) => it.driverCount)} labels={totalDriversPerCnh.map((it: any) => it.categoryName)} type="polarArea" isLoading={isLoading} />
              </Box>
            </TotalItemCard>
          </Box>

          <Box className="tw-min-w-0 tw-text-center">
            <TotalItemCard withAdornment={false} isLoading={isLoading} title={"de Chamados por Gravidade"}>
              <Box className="tw-flex tw-w-full tw-min-w-0 tw-justify-center">
                <TotalDonutChart data={totalTicketsByGravity.length > 0 ? totalTicketsByGravity.map((it: any) => it.ticketCount) : [1, 1, 1]} labels={totalTicketsByGravity.length > 0 ? totalTicketsByGravity.map((it: any) => it.gravityName) : ["Grave", "Média", "Leve"]} type="donut" isLoading={isLoading} />
              </Box>
            </TotalItemCard>
          </Box>
      </Box>
      {/*-----------------------*/}

      <Box sx={chartGridSx}>
          <Box className="tw-min-w-0 tw-text-center">
            <TotalItemCard withAdornment={false} isLoading={isLoading} title={"de Veículos por Cliente"}>
              <Box className="tw-flex tw-w-full tw-min-w-0 tw-justify-center">
                <TotalDonutChart data={totalVehiclesByCustomer.map((it: any) => it.vehicleCount)} labels={totalVehiclesByCustomer.map((it: any) => it.customerName)} type="donut" isLoading={isLoading} />
              </Box>
            </TotalItemCard>
          </Box>

          <Box className="tw-min-w-0 tw-text-center">
            <TotalItemCard withAdornment={false} isLoading={isLoading} title={"de Dispositivos por Fabricante"}>
              <Box className="tw-flex tw-w-full tw-min-w-0 tw-justify-center">
                <TotalDonutChart data={totalDevicesByManufacturer.map((it: any) => it.deviceCount)} labels={totalDevicesByManufacturer.map((it: any) => it.manufacturerName)} type="pie" isLoading={isLoading} />
              </Box>
            </TotalItemCard>
          </Box>

          <Box className="tw-min-w-0 tw-text-center">
            <TotalItemCard withAdornment={false} isLoading={isLoading} title={"de Veículos por Status"}>
              <Box className="tw-flex tw-w-full tw-min-w-0 tw-justify-center">
                <TotalDonutChart data={totalVehiclesByStatus.map((it: any) => it.vehicleCount)} labels={totalVehiclesByStatus.map((it: any) => it.status)} type="donut" isLoading={isLoading} />
              </Box>
            </TotalItemCard>
          </Box>
      </Box>
    </Box>
  );
};

export default SystemTab;
